import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHasSelectedProducts } from "../../hooks/useHasSelectedProducts";
import { SwitchViewMode } from "../../shared/enums/SwitchViewMode";
import { action } from "../../store/action";
import { MachineTonnage } from "../../store/interfaces/MachineTonnage";
import ConfirmDialog from "../Dialog/ConfirmDialog";
import { useStateCleaner } from "../Products/hooks/useStateCleaner";
import MachineSearch from "./MachineSearch";
import "./MachineTonnageSwitch.scss";
import Tonnage from "./Tonnage";

interface MachineTonnageSwitchProps {
  machineTonnage: MachineTonnage;
}

const MachineTonnageSwitch = ({
  machineTonnage,
}: MachineTonnageSwitchProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["machinetonnagestep", "popup"]);
  const hasSelectedProducts = useHasSelectedProducts();
  const { clearStateAllProducts } = useStateCleaner();
  const [selectedSwitchViewMode, setSelectedSwitchViewMode] =
    useState<SwitchViewMode>(undefined);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<SwitchViewMode>(
    machineTonnage ? machineTonnage.switchViewMode : SwitchViewMode.Switch
  );

  const onMachineSearchClick = () => {
    switchViewMode(SwitchViewMode.MachineSearch);
  };

  const onTonnageClick = () => {
    switchViewMode(SwitchViewMode.Tonnage);
  };

  const onClickSwitchFromMachineSearch = () => {
    if (hasSelectedProducts) {
      setSelectedSwitchViewMode(SwitchViewMode.Tonnage);
      setShowDialog(true);
      return;
    }

    switchViewMode(SwitchViewMode.Tonnage);
  };

  const onClickSwitchFromTonnage = () => {
    if (hasSelectedProducts) {
      setSelectedSwitchViewMode(SwitchViewMode.MachineSearch);
      setShowDialog(true);
      return;
    }

    switchViewMode(SwitchViewMode.MachineSearch);
  };

  const onDialogClose = () => {
    setShowDialog(false);
  };

  const switchViewMode = (switchViewMode?: SwitchViewMode) => {
    if (switchViewMode) {
      setViewMode(switchViewMode);
    } else {
      setViewMode(selectedSwitchViewMode);
    }

    const machineTonnage: MachineTonnage = {
      machineModel: undefined,
      switchViewMode: selectedSwitchViewMode,
      tonnage: undefined,
    };

    dispatch(action.saveMachineTonnage(machineTonnage));
    dispatch(action.saveWizardEnableNext(false));
    if (showDialog) {
      clearStateAllProducts();
    }
    setSelectedSwitchViewMode(undefined);
    setShowDialog(false);
  };

  return (
    <>
      <div className="switchMachineTonnageContainer">
        {viewMode === SwitchViewMode.Switch && (
          <>
            <div className="switchMachineSearch">
              <h2 className="switchH2" onClick={onMachineSearchClick}>
                {t("machinetonnagetexts.findmachine")}
                <NavigateNextIcon />
              </h2>
              <div>{t("machinetonnagetexts.findmachinetext1")}</div>
            </div>
            <div className="switchTonnage">
              <h2 className="switchH2" onClick={onTonnageClick}>
                {t("machinetonnagetexts.picktonnage")}
                <NavigateNextIcon />
              </h2>
              <div className="switchTonnagePreample">
                {t("machinetonnagetexts.picktonnagetext1")}
              </div>
            </div>
          </>
        )}
        {viewMode === SwitchViewMode.MachineSearch && (
          <MachineSearch
            currentMachineModel={machineTonnage.machineModel}
            hasSelectedProducts={hasSelectedProducts}
            onClick={onClickSwitchFromMachineSearch}
          />
        )}
        {viewMode === SwitchViewMode.Tonnage && (
          <Tonnage
            currentTonnage={machineTonnage.tonnage}
            hasSelectedProducts={hasSelectedProducts}
            onClick={onClickSwitchFromTonnage}
          />
        )}
      </div>
      <ConfirmDialog
        dialogHeader={t("important", { ns: "popup" })}
        text={t("areyousure", { ns: "popup" })}
        okButtonText={t("clearchoises", { ns: "popup" })}
        cancelButtonText={t("cancel", { ns: "popup" })}
        open={showDialog}
        onClose={onDialogClose}
        onCancelClick={onDialogClose}
        onOkClick={() => switchViewMode()}
      ></ConfirmDialog>
    </>
  );
};

export default MachineTonnageSwitch;
